// Handlekorg for Butikk – éi korg per arrangør (betalinga går direkte til seljaren, så éi bestilling = éin seljar).
// Lagra i localStorage; alle faner/komponentar held seg synkroniserte via eit eige vindauge-event + storage-event.
import { useCallback, useEffect, useState } from "react";

const KEY = "rightin.shop.cart.v1";
const EVENT = "rightin-cart-changed";
export const CART_MAX_LINES = 10;
export const CART_MAX_QTY = 20;

const empty = () => ({ organizer_id: "", organizer_name: "", items: [] });

export function readCart() {
  if (typeof window === "undefined") return empty();
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return empty();
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return empty();
    return { organizer_id: parsed.organizer_id || "", organizer_name: parsed.organizer_name || "", items: parsed.items.filter((i) => i && i.product_id && i.quantity > 0) };
  } catch {
    return empty();
  }
}

function writeCart(cart) {
  try {
    if (!cart.items.length) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, JSON.stringify(cart));
  } catch {
    /* privat modus / full lagring – korga lever då berre i minnet for denne sida */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: cart }));
}

export function cartCount(cart) {
  return (cart?.items || []).reduce((sum, i) => sum + (i.quantity || 0), 0);
}

export function cartSubtotal(cart) {
  return (cart?.items || []).reduce((sum, i) => sum + (i.quantity || 0) * (i.price || 0), 0);
}

/** Legg til vare. Returnerer { ok, reason } – reason "other_organizer" når korga alt høyrer til ein annan arrangør (kallar må spørje om byte). */
export function addToCart(product, organizer, quantity = 1, { replace = false } = {}) {
  const cart = readCart();
  const qty = Math.max(1, Math.min(CART_MAX_QTY, Math.round(quantity || 1)));
  if (cart.items.length && cart.organizer_id && cart.organizer_id !== product.organizer_id) {
    if (!replace) return { ok: false, reason: "other_organizer", cart };
    cart.items = [];
  }
  const next = { organizer_id: product.organizer_id, organizer_name: organizer?.org_name || product.organizer_name || cart.organizer_name || "", items: [...cart.items] };
  const idx = next.items.findIndex((i) => i.product_id === product.id);
  const max = Math.min(CART_MAX_QTY, product.max_per_order || CART_MAX_QTY);
  if (idx >= 0) {
    next.items[idx] = { ...next.items[idx], quantity: Math.min(max, next.items[idx].quantity + qty), price: product.price, name: product.name, image_url: product.image_url || "" };
  } else {
    if (next.items.length >= CART_MAX_LINES) return { ok: false, reason: "too_many_lines", cart };
    next.items.push({ product_id: product.id, name: product.name, price: product.price, image_url: product.image_url || "", quantity: Math.min(max, qty) });
  }
  writeCart(next);
  return { ok: true, cart: next };
}

export function setCartQuantity(productId, quantity) {
  const cart = readCart();
  const items = cart.items.map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(1, Math.min(CART_MAX_QTY, Math.round(quantity || 1))) } : i));
  const next = { ...cart, items };
  writeCart(next);
  return next;
}

export function removeFromCart(productId) {
  const cart = readCart();
  const next = { ...cart, items: cart.items.filter((i) => i.product_id !== productId) };
  if (!next.items.length) Object.assign(next, empty());
  writeCart(next);
  return next;
}

/** Oppdater pris/namn/bilete frå ferske produktdata og fjern varer som ikkje lenger kan kjøpast. Returnerer { cart, removed: [names] }. */
export function reconcileCart(products) {
  const cart = readCart();
  const byId = new Map((products || []).map((p) => [p.id, p]));
  const removed = [];
  const items = [];
  for (const it of cart.items) {
    const p = byId.get(it.product_id);
    if (!p || p.stock_status === "sold_out" || !p.max_per_order) {
      removed.push(it.name);
      continue;
    }
    items.push({ ...it, name: p.name, price: p.price, image_url: p.image_url || "", quantity: Math.min(it.quantity, p.max_per_order, CART_MAX_QTY), max: Math.min(p.max_per_order, CART_MAX_QTY) });
  }
  const next = items.length ? { ...cart, items } : empty();
  if (removed.length || JSON.stringify(next.items) !== JSON.stringify(cart.items)) writeCart(next);
  return { cart: next, removed };
}

export function clearCart() {
  writeCart(empty());
}

/** React-hook: korg + teljar, oppdatert ved endringar i denne eller andre faner. */
export function useCart() {
  const [cart, setCart] = useState(readCart);
  useEffect(() => {
    const onChange = () => setCart(readCart());
    window.addEventListener(EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  const refresh = useCallback(() => setCart(readCart()), []);
  return { cart, count: cartCount(cart), subtotal: cartSubtotal(cart), refresh };
}
